<main>
  <loading-overlay [isLoading]="isLoading() > 0" [fullWidth]="true"></loading-overlay>
  <p-toast position="bottom-right" key="br"></p-toast>
  <p-confirmDialog />

  <header>
    <div class="headline row-start-center gap-[1rem]">
      <h1>{{ headerTitle() }}</h1>
      @if (isPersisted()) {
        <p-tag [severity]="statusSeverity()" [value]="statusLabelText()" />
      }
    </div>
    <div class="buttons">
      <span class="separator"></span>
      @if (isSetupPhase()) {
        <p-button [label]="'Withholding_Create' | translateWF: 'Create certificate'" icon="pi pi-plus" severity="success" (click)="createCertificate()" />
      }
      @if (isPersisted() && isDraft()) {
        <p-button [label]="'SaveDraft' | translateWF: 'Save draft'" icon="pi pi-save" variant="outlined" [disabled]="!canSaveDraft()" (click)="saveDraft()" />
        <p-button [label]="'Withholding_Post' | translateWF: 'Post certificate'" icon="pi pi-check" severity="success" [disabled]="!canPost()" (click)="post()" />
      }
      @if (hasConstanciaNumber()) {
        <p-button
          [label]="'Withholding_PrintCertificate' | translateWF: 'Print certificate'"
          icon="pi pi-print"
          variant="outlined"
          [disabled]="isPrinting()"
          (click)="printCertificate()"
        />
      }
      <p-button [label]="'Back' | translateWF: 'Back'" variant="outlined" icon="pi pi-arrow-left" (click)="back()" />
    </div>
  </header>

  <div class="content">
    <form [formGroup]="form" class="grid-12 gap-[1.5rem]">
      <!-- LEFT COLUMN -->
      <div class="span-12-12-8 col">
        <!-- 1 · Certificate setup: the provider and the ONE tax -->
        <p-panel [header]="'Withholding_Section_Setup' | translateWF: 'Certificate'" [toggleable]="true">
          <p-card>
            @if (isSetupPhase()) {
              <div [formGroup]="setupForm" class="grid-12">
                <div class="span-12">
                  <div class="form-box">
                    <label for="provider">{{ setupFieldLabels.provider | translateWF: 'Provider' }}</label>
                    <p-autoComplete
                      id="provider"
                      formControlName="provider"
                      #providerSearch="cslServerSearch"
                      [cslServerSearch]="fetchProviders"
                      [suggestions]="providerSearch.results()"
                      (onSelect)="onProviderSelected($event.value)"
                      (onClear)="onProviderCleared()"
                      optionLabel="name"
                      [forceSelection]="true"
                      [dropdown]="true"
                      dropdownIcon="pi pi-search"
                      [minLength]="1"
                      appendTo="body"
                      class="w-full"
                      styleClass="w-full"
                      [ngClass]="{ 'ng-invalid ng-dirty': setupForm.controls.provider.invalid && setupForm.controls.provider.touched }"
                    >
                      <ng-template let-provider pTemplate="item">
                        <div class="col">
                          <span
                            ><strong>{{ provider.code }}</strong> — {{ provider.name }}</span
                          >
                        </div>
                      </ng-template>
                    </p-autoComplete>
                  </div>
                </div>

                <div class="span-12">
                  <div class="form-box">
                    <label for="withholdingCode">{{ setupFieldLabels.withholdingCode | translateWF: 'Type of tax' }}</label>
                    <p-select
                      id="withholdingCode"
                      formControlName="withholdingCode"
                      [options]="usableCodeOptions()"
                      optionLabel="label"
                      optionValue="value"
                      appendTo="body"
                      styleClass="w-full"
                      [placeholder]="'Withholding_SelectCode' | translateWF: 'Select the tax being withheld'"
                      [ngClass]="{ 'ng-invalid ng-dirty': setupForm.controls.withholdingCode.invalid && setupForm.controls.withholdingCode.touched }"
                    />
                    <small class="text-surface-500">
                      {{
                        'Withholding_Code_Hint'
                          | translateWF
                            : 'One tax per certificate — it is what lets several AP
                    documents share one.'
                      }}
                    </small>
                  </div>
                </div>
              </div>

              <!-- Dates belong to `form`, NOT `setupForm` — they must sit outside the setupForm group above,
                   or formControlName resolves against the wrong FormGroup and throws at runtime. -->
              <div class="grid-12 mt-4">
                <div class="span-12-6">
                  <div class="form-box">
                    <label>{{ fieldLabels.documentDate | translateWF: 'Document date' }}</label>
                    <!-- Read-only, always today: a Constancia is dated the day it is issued, and that is the
                         date SAR reads it by. Disabled rather than hidden so the officer sees what it carries. -->
                    <p-datepicker formControlName="documentDate" appendTo="body" styleClass="w-full" />
                  </div>
                </div>
                <div class="span-12-6">
                  <div class="form-box">
                    <label>{{ fieldLabels.postingDate | translateWF: 'Posting date' }}</label>
                    <p-datepicker formControlName="postingDate" appendTo="body" styleClass="w-full" />
                  </div>
                </div>
              </div>
            } @else {
              <div class="grid-12-sm provider-info">
                <div class="span-6-3">
                  <span class="provider-info__label">{{ 'ApDocument_Provider' | translateWF: 'Provider' }}</span>
                  <span class="provider-info__value">{{ certificateProviderName() || '—' }}</span>
                </div>
                <div class="span-6-3">
                  <span class="provider-info__label">{{ 'Withholding_Code' | translateWF: 'Type of tax' }}</span>
                  <span class="provider-info__value">{{ withholdingCodeText() || '—' }} ({{ withholdingRate() }}%)</span>
                </div>
                <div class="span-6-3">
                  <span class="provider-info__label">{{
                    'Withholding_ProviderJurisdiction'
                      | translateWF
                        : 'Provider
                  jurisdiction'
                  }}</span>
                  <span class="provider-info__value">{{ providerCountryCode() || '—' }}</span>
                </div>
                <div class="span-6-3">
                  <span class="provider-info__label">{{
                    'ApDocument_Withholding_Residency'
                      | translateWF
                        : 'Provider
                  residency'
                  }}</span>
                  <span class="provider-info__value">{{ residencyLabel(withholdingResidency()) }}</span>
                </div>
                <div class="span-6-3">
                  <span class="provider-info__label">{{ fieldLabels.documentDate | translateWF: 'Document date' }}</span>
                  <span class="provider-info__value">{{ form.controls.documentDate.value | date: 'dd/MM/yyyy' }}</span>
                </div>
                <!-- Posting date stays editable on a draft: a certificate opened today may not be posted until
                     tomorrow, and the period it lands in is the officer's call. -->
                <div class="span-6-3">
                  @if (isDraft()) {
                    <div class="form-box">
                      <label>{{ fieldLabels.postingDate | translateWF: 'Posting date' }}</label>
                      <p-datepicker formControlName="postingDate" appendTo="body" styleClass="w-full" />
                    </div>
                  } @else {
                    <span class="provider-info__label">{{ fieldLabels.postingDate | translateWF: 'Posting date' }}</span>
                    <span class="provider-info__value">{{ form.controls.postingDate.value | date: 'dd/MM/yyyy' }}</span>
                  }
                </div>
                <div class="span-12">
                  <p-message severity="info" [text]="shapeHint()" styleClass="w-full" />
                </div>
              </div>
            }
          </p-card>
        </p-panel>

        <!-- 2 · The AP documents this certificate covers -->
        @if (isPersisted()) {
          <p-panel [header]="'Withholding_Section_Documents' | translateWF: 'AP documents'" [toggleable]="true">
            <p-card>
              @if (canAddSource()) {
                <div class="form-box mb-4">
                  <label for="apDocument">{{ 'Withholding_AddDocument' | translateWF: 'Add an AP document' }}</label>
                  <p-selectButton
                    [options]="documentSearchFieldOptions"
                    [ngModel]="documentSearchField()"
                    [ngModelOptions]="{ standalone: true }"
                    (onChange)="documentSearchField.set($event.value)"
                    optionLabel="label"
                    optionValue="value"
                    [allowEmpty]="false"
                    styleClass="mb-2"
                  />
                  <p-autoComplete
                    id="apDocument"
                    [ngModel]="documentPickerValue()"
                    [ngModelOptions]="{ standalone: true }"
                    #documentSearch="cslServerSearch"
                    [cslServerSearch]="fetchDocuments"
                    [suggestions]="documentSearch.results()"
                    (onSelect)="onDocumentSelected($event.value)"
                    optionLabel="number"
                    [forceSelection]="true"
                    [dropdown]="true"
                    dropdownIcon="pi pi-search"
                    [minLength]="1"
                    appendTo="body"
                    class="w-full"
                    styleClass="w-full"
                  >
                    <ng-template let-doc pTemplate="item">
                      <div class="col">
                        <span
                          ><strong>{{ doc.number }}</strong> — {{ doc.providerName }}</span
                        >
                        <small class="text-surface-500">
                          {{ 'Withholding_FiscalInvoice' | translateWF: 'Fiscal #' }}: {{ doc.fiscalNumber || '—' }} · {{ doc.balanceDue | number: '1.2-2' }}
                          {{ currencyLabel(doc.currency) }}
                        </small>
                      </div>
                    </ng-template>
                  </p-autoComplete>
                </div>
              } @else if (isDraft() && isLocalProvider() && sourceCount() > 0) {
                <p-message
                  severity="info"
                  [text]="'Withholding_Shape_LocalFull' | translateWF: 'This certificate already covers its one AP document. Issue a separate Constancia for another.'"
                  styleClass="w-full mb-4"
                />
              }

              @if (sourceCount() === 0) {
                <p class="text-surface-500">{{ 'Withholding_NoDocuments' | translateWF: 'No AP documents attached yet.' }}</p>
              }

              <!-- Every line needs an explicit answer. A default would be a decision made on the officer's
                   behalf and signed with their name, on a fiscal document. -->
              @if (hasUndecidedLines()) {
                <p-message
                  severity="warn"
                  styleClass="w-full mb-4"
                  [text]="'Withholding_UndecidedLines' | translateWF: 'Every line needs a decision (Withhold, Exempt, or Remove) before this certificate can be posted.'"
                />
              }

              <!-- 3 · Lines to withhold, grouped by the document they came from -->
              <div formArrayName="sources" class="col">
                @for (sourceRow of sources.controls; track sourceRow; let sourceIndex = $index) {
                  @let source = getSourceGroup(sourceRow);
                  <div [formGroupName]="sourceIndex" class="source-block">
                    <div class="source-block__head">
                      <div class="col-sm">
                        <!-- The shared identity block (#4788): the provider's fiscal number on top — what the
                             paper Constancia legally cites — and our AP number underneath as the link. -->
                        <app-document-identity
                          [documentNumber]="source.controls.documentNumber.value"
                          [fiscalNumber]="source.controls.fiscalNumber.value"
                          [linkTitle]="'ApDocument_QuickView_Open' | translateWF: 'View document detail'"
                          (documentClick)="openDocumentQuickView(source)"
                        >
                          <p-tag severity="secondary" [value]="source.controls.lines.length + ' ' + ('Withholding_Lines' | translateWF: 'line(s)')" />
                        </app-document-identity>
                      </div>

                      <div class="row-start-center">
                        @if (!sourceIsLocal(source)) {
                          <!-- Each document converts at the rate in force on ITS OWN issue date, so a certificate
                             spanning several days carries several rates. Editable because the officer may hold a
                             different quote for that day. -->
                          <div class="form-box form-box--inline">
                            <label>
                              {{ 'Withholding_LocalRate' | translateWF: 'Rate to' }} {{ localCurrencyText() }}
                              @if (source.controls.rateDate.value) {
                                <!-- Which day's rate this is. A certificate gathering invoices issued on
                                   different days legitimately carries different rates, and without the date
                                   a column of different numbers reads as an inconsistency. -->
                                <span class="rate-date">{{ source.controls.rateDate.value | date: 'dd/MM/yyyy' }}</span>
                              }
                            </label>
                            <p-inputGroup>
                              <p-inputNumber formControlName="localRate" mode="decimal" [minFractionDigits]="4" [maxFractionDigits]="rateDecimals" [min]="0" />
                              <p-inputGroupAddon>{{ localCurrencyText() }}/{{ currencyLabel(source.controls.currency.value) }}</p-inputGroupAddon>
                            </p-inputGroup>
                          </div>
                        }
                        <div class="text-right">
                          <span class="source-block__label">{{ 'Withholding_Retained' | translateWF: 'Retained' }}</span>
                          <span class="source-block__total"> {{ sourceRetainedLocal(source) | number: '1.2-2' }} {{ localCurrencyText() }} </span>
                        </div>
                        @if (isDraft()) {
                          <!-- Brings back lines removed in an earlier save. Decisions already made survive it. -->
                          <p-button
                            icon="pi pi-replay"
                            severity="secondary"
                            variant="outlined"
                            [pTooltip]="
                              'Withholding_ResetLines_Tooltip'
                                | translateWF: 'Restore this document’s lines, including any removed earlier. Decisions you have already made are kept.'
                            "
                            (click)="resetSourceLines(source)"
                          />
                          <p-button
                            icon="pi pi-trash"
                            severity="danger"
                            variant="outlined"
                            [pTooltip]="'Withholding_RemoveDocument' | translateWF: 'Remove this AP document'"
                            (click)="removeSource(source)"
                          />
                        }
                      </div>
                    </div>

                    <p-table [value]="source.controls.lines.controls" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '48rem' }">
                      <ng-template pTemplate="header">
                        <tr>
                          <th>{{ 'ApDocument_Line_Description' | translateWF: 'Description' }}</th>
                          <th class="text-right" style="width: 11rem">{{ 'Withholding_Base' | translateWF: 'Base' }}</th>
                          <th class="text-right" style="width: 11rem">{{ 'Withholding_BaseLocal' | translateWF: 'Base' }} ({{ localCurrencyText() }})</th>
                          <th class="text-right" style="width: 11rem">{{ 'Withholding_Retained' | translateWF: 'Retained' }}</th>
                          <th style="width: 15rem">{{ 'Withholding_Decision' | translateWF: 'Decision' }}</th>
                        </tr>
                      </ng-template>

                      <ng-template pTemplate="body" let-row>
                        @let line = getLineGroup(row);
                        @let removed = line.controls.decision.value === 'remove';
                        <tr [formGroup]="line" [class.line--undecided]="!line.controls.decision.value" [class.line--removed]="removed">
                          <td>{{ line.controls.description.value || '—' }}</td>
                          <td class="text-right">{{ line.controls.baseAmount.value | number: '1.2-2' }} {{ currencyLabel(source.controls.currency.value) }}</td>
                          <td class="text-right text-surface-500">{{ line.controls.localBaseAmount.value | number: '1.2-2' }}</td>
                          <td class="text-right font-medium">
                            @switch (line.controls.decision.value) {
                              @case ('withhold') {
                                −{{ lineLocalAmount(source, line) | number: '1.2-2' }} {{ localCurrencyText() }}
                              }
                              @case ('exempt') {
                                <span class="text-surface-400">{{ 'Withholding_NoAplica' | translateWF: 'No aplica' }}</span>
                              }
                              @default {
                                <span class="text-surface-400">—</span>
                              }
                            }
                          </td>
                          <td>
                            @if (isDraft()) {
                              <!-- Withhold / Exempt / Remove. Exempt COVERS the line and withholds nothing, so
                                   the AP document can still be paid; Remove takes it off the certificate, where
                                   it stays owed under another code and keeps the document blocked. -->
                              <p-selectButton
                                formControlName="decision"
                                [options]="decisionOptions"
                                optionLabel="label"
                                optionValue="value"
                                [allowEmpty]="false"
                                styleClass="decision-toggle"
                              />
                              @if (!line.controls.decision.value) {
                                <small class="block mt-1 text-amber-600">{{
                                  'Withholding_NeedsDecision'
                                    | translateWF
                                      : 'Needs a
                          decision'
                                }}</small>
                              }
                              @if (removed) {
                                <small class="block mt-1 text-surface-500">
                                  {{
                                    'Withholding_Removed_Hint'
                                      | translateWF
                                        : 'Leaves the certificate on save. The AP document
                          stays blocked.'
                                  }}
                                </small>
                              }
                            } @else {
                              <span class="text-surface-500">{{
                                line.controls.decision.value === 'exempt'
                                  ? ('Withholding_NoAplica' | translateWF: 'No aplica')
                                  : ('Withholding_Decision_Withhold' | translateWF: 'Withhold')
                              }}</span>
                            }
                          </td>
                        </tr>
                      </ng-template>

                      <!-- Lines a POSTED certificate already holds. Read-only, and named: without this a line
                           the officer removed and tried to restore would simply be missing, with no way to tell
                           whether it failed or went somewhere. -->
                      <ng-template pTemplate="footer">
                        @for (claimed of claimedLinesFor(source); track claimed.sourceLineNumber) {
                          <tr class="line--claimed">
                            <td>
                              <span class="text-surface-500">{{ claimed.description || '—' }}</span>
                              <p-tag
                                severity="secondary"
                                styleClass="ml-2"
                                icon="pi pi-lock"
                                [value]="('Withholding_AlreadyWithheld' | translateWF: 'Already withheld') + ' ' + (claimed.certificateNumber || '')"
                              />
                            </td>
                            <td class="text-right text-surface-500">{{ claimed.baseAmount | number: '1.2-2' }} {{ currencyLabel(source.controls.currency.value) }}</td>
                            <td class="text-right text-surface-500">{{ claimed.localBaseAmount | number: '1.2-2' }}</td>
                            <td class="text-right text-surface-400">—</td>
                            <td class="text-surface-400">{{ 'Withholding_ReadOnly' | translateWF: 'Read-only' }}</td>
                          </tr>
                        }
                      </ng-template>

                      <ng-template pTemplate="emptymessage">
                        <tr>
                          <td colspan="5" class="text-center">
                            {{ 'Withholding_NoLines' | translateWF: 'No lines left on this document.' }}
                          </td>
                        </tr>
                      </ng-template>
                    </p-table>
                  </div>
                }
              </div>
            </p-card>
          </p-panel>

          <!-- Notes -->
          <p-panel [header]="'ApDocument_Section_Notes' | translateWF: 'Notes'" [toggleable]="true" [collapsed]="true">
            <p-card>
              <div class="form-box">
                <textarea pTextarea formControlName="notes" rows="3" class="w-full"></textarea>
              </div>
            </p-card>
          </p-panel>
        }
      </div>

      <!-- RIGHT COLUMN -->
      @if (isPersisted()) {
        <div class="span-12-12-4 col sticky-summary">
          <!-- Jurisdiction -->
          <p-panel [header]="'Withholding_Section_Scope' | translateWF: 'Withholding'" [toggleable]="true">
            <p-card data-variant="fields">
              <div class="form-box">
                <label>{{ 'ApDocument_Withholding_Country' | translateWF: 'Jurisdiction (country)' }}</label>
                <!-- Ours, not theirs: we only withhold Honduran taxes, whatever the provider's domicile. -->
                <span class="rate-readout">{{ withholdingCountry }}</span>
              </div>
              <div class="span-6">
                <label>{{ 'Withholding_Code' | translateWF: 'Type of tax' }}</label>
                <span class="rate-readout">{{ withholdingCodeText() || '—' }} ({{ withholdingRate() }}%)</span>
              </div>
            </p-card>
          </p-panel>

          <!-- Constancia fiscal identity (self-issued No. Retención + CAI, drawn on post — #4487) -->
          @if (hasConstanciaNumber()) {
            <p-panel [header]="'Withholding_Section_Constancia' | translateWF: 'Withholding certificate'">
              <p-card data-variant="fields">
                <div class="form-box">
                  <label>{{ 'Withholding_ConstanciaNumber' | translateWF: 'No. Retención' }}</label>
                  <span class="rate-readout">{{ constanciaNumber() }}</span>
                </div>
                <div class="form-box">
                  <label>{{ 'ApDocument_Cai' | translateWF: 'CAI' }}</label>
                  <span class="rate-readout">{{ constanciaCai() || '—' }}</span>
                </div>
              </p-card>
            </p-panel>
          }

          <!-- Summary — Lempira, the one figure that stays single-valued across mixed-currency sources -->
          <p-panel [header]="'ApDocument_Section_Summary' | translateWF: 'Summary'">
            <p-card>
              <div class="summary-rows">
                <div class="summary-rows__row">
                  <span>{{ 'Withholding_Documents' | translateWF: 'AP documents' }}</span>
                  <span>{{ sourceCount() }}</span>
                </div>
                <div class="summary-rows__row">
                  <span>{{ 'Withholding_Base' | translateWF: 'Base' }}</span>
                  <span>{{ totalBaseLocal() | number: '1.2-2' }} {{ localCurrencyText() }}</span>
                </div>
                <div class="summary-rows__row">
                  <span>{{ 'ApDocument_Withholding' | translateWF: 'Withholding' }}</span>
                  <span>−{{ totalWithheldLocal() | number: '1.2-2' }} {{ localCurrencyText() }}</span>
                </div>
                <div class="summary-rows__row summary-rows__row--total">
                  <span>{{ 'ApDocument_NetToProvider' | translateWF: 'Net' }}</span>
                  <span>{{ totalNetLocal() | number: '1.2-2' }} {{ localCurrencyText() }}</span>
                </div>
              </div>
              <small class="block mt-2 text-surface-500">
                {{
                  'Withholding_LempiraHint'
                    | translateWF
                      : 'The retention is owed to SAR in Lempira, whatever the
              invoices
              were billed in.'
                }}
              </small>
            </p-card>
          </p-panel>

          <!-- Accounting (#5024 #5031) -->
          @if (showAccounting()) {
            <p-panel [header]="'Withholding_Section_Accounting' | translateWF: 'Accounting'">
              <p-card>
                <div class="summary-rows">
                  <div class="summary-rows__row row-start-center">
                    <span>{{ 'ApDocument_JournalEntry' | translateWF: 'Journal entry' }}</span>
                    <span class="ml-auto font-mono">{{ journalEntryDocumentNumber() }}</span>
                  </div>
                </div>

                <span class="block mt-3 mb-2 text-[0.7rem] font-semibold uppercase tracking-wide text-surface-500">
                  {{ 'ApDocument_GlImpact' | translateWF: 'GL impact' }}
                </span>
                <app-journal-entry-summary [references]="journalEntryReferences()" [currencyLabel]="localCurrencyText()" />
              </p-card>
            </p-panel>
          }
        </div>
      }
    </form>
  </div>
</main>
